/**
 * The management period, kept in the URL (P4).
 *
 * ## Why the URL and not component state
 *
 * The period is the one control every figure on the Unternehmensübersicht answers
 * to, and until this slice it lived in a `useState` inside `AdminUebersichtPage`.
 * So a reload dropped it, a link to "our year so far" could not be sent, and
 * Back walked out of the page rather than back to the previous selection. Now the
 * URL IS the state: there is no `useState`, no synchronising effect, and
 * therefore no state→URL→state loop to get wrong. A selection is one
 * `setSearchParams` call, one render, one request round.
 *
 * No localStorage. A stored period and a linked period would disagree the moment
 * someone opened a shared link, and there would be no rule for which of the two
 * wins.
 *
 * ## The contract
 *
 *     (no `period` parameter)                                  the default period
 *     ?period=current|prev|last3|last6|last12|ytd|prevYear|all  a preset, by INTENT
 *     ?period=month&period_month=2026-09                        one month, absolute
 *     ?period=custom&period_from=2026-04&period_to=2026-09       a range, absolute
 *
 * ### A preset stores its intent, not its dates
 *
 * `?period=ytd` — not `from=2026-01&to=2026-09`. "Dieses Jahr" means *this* year
 * to *today*, so it is re-derived from the Swiss business month on every read and
 * stays correct as the months pass. Storing its resolved bounds instead would
 * freeze a rolling window into a fixed one that merely looked right on the day it
 * was copied, and — the part that actually matters — it would arrive back
 * indistinguishable from a hand-picked range with the same bounds, which is the
 * distinction this whole slice exists to keep.
 *
 * ### A custom range stores absolute bounds, and stays custom
 *
 * `?period=custom&period_from=2026-01&period_to=2026-09` reads back as exactly
 * those months, as a CUSTOM period. It is never re-recognised as `ytd` or `last6`
 * because its dates happen to line up. Intent beats arithmetic in both
 * directions.
 *
 * ### Why `month` exists as its own form
 *
 * `mode: "month"` is not cosmetic: `selectPerformanceRequest` sends `?year=&month=`
 * for it and the range form for everything else, and `AdminUebersichtPage` derives
 * `isRange` from it. Round-tripping a single month through the `custom` form would
 * flip the mode and silently change the request, so the mode is preserved
 * explicitly. The picker itself reaches `mode: "month"` only through the `current`
 * and `prev` presets, which serialise as presets; this form is what keeps a
 * hand-written or programmatic single month honest.
 *
 * ### Why the keys are prefixed
 *
 * `period_from` rather than `from`. These parameters ride on the admin router
 * alongside pages that own their own single-word query state (`view`, `source` on
 * the invoice dashboard), and a bare `from`/`to`/`month` is exactly the name a
 * future filter would reach for. The invoice dashboard reads neither name today,
 * so its independence is a fact rather than a promise — the prefix keeps it one.
 */
import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { isPeriodPresetKey, presetToPeriod } from "./uebersichtHelpers";

/** Which period is meant: a preset key, or the shape of an absolute selection. */
export const PERIOD_PARAM = "period";
/** `YYYY-MM`, with `period=month`. */
export const PERIOD_MONTH_PARAM = "period_month";
/** `YYYY-MM`, with `period=custom`. */
export const PERIOD_FROM_PARAM = "period_from";
/** `YYYY-MM`, with `period=custom`. */
export const PERIOD_TO_PARAM = "period_to";

/**
 * Every key this module owns — and the exact set it may delete.
 *
 * Writing a period clears these four and then writes the ones the new period
 * needs, so switching from a range to a preset cannot leave `period_from`
 * stranded beside `period=ytd`. Everything NOT on this list is copied through
 * untouched: the period does not get to decide what else a URL may carry.
 */
export const PERIOD_PARAMS = [
  PERIOD_PARAM,
  PERIOD_MONTH_PARAM,
  PERIOD_FROM_PARAM,
  PERIOD_TO_PARAM,
];

/** The two shapes `period` can take that are not preset keys. */
const CUSTOM_TOKEN = "custom";
const MONTH_TOKEN = "month";

const MONTH_TOKEN_RE = /^(\d{4})-(\d{2})$/;

/**
 * The year range a month token may name.
 *
 * Matched to the backend's own `Query(..., ge=2020, le=2100)` on every period
 * contract rather than chosen here. A URL outside it would parse cleanly and then
 * produce a 422, and "no invalid API request" is the point of validating at all.
 */
const MIN_YEAR = 2020;
const MAX_YEAR = 2100;

/** `"2026-09"` → `{year: 2026, month: 9}`, or `null` for anything else. */
export function parseMonthToken(raw) {
  if (typeof raw !== "string") return null;
  const match = MONTH_TOKEN_RE.exec(raw.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  // `00` and `13` both match the pattern — the pattern fixes the WIDTH, this
  // fixes the meaning.
  if (month < 1 || month > 12) return null;
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  return { year, month };
}

/** `{year: 2026, month: 9}` → `"2026-09"`. */
export function monthToken(value) {
  return `${value.year}-${String(value.month).padStart(2, "0")}`;
}

const isMonthValue = (value) =>
  Boolean(value) && Number.isFinite(Number(value.year)) && Number.isFinite(Number(value.month));

const ordinal = (value) => Number(value.year) * 12 + Number(value.month);

/**
 * The period a URL with no period parameters means.
 *
 * The current month, which is what the page has always defaulted to — expressed
 * as the `current` preset rather than rebuilt, so the two can never drift apart
 * and the picker shows "Aktueller Monat" as selected on arrival, exactly as it
 * did before this slice.
 */
export function defaultPeriod(now) {
  return presetToPeriod("current", now);
}

/**
 * The query parameters that represent `period`, as a plain object.
 *
 * Returns `{}` for a period it cannot express — the caller then writes no period
 * keys at all, which reads back as the default. That is a better failure than a
 * half-written `?period=custom` with no bounds, which would read back as invalid.
 */
export function serializePeriod(period) {
  const presetKey = period?.presetKey;
  // Identity first, and deliberately before `mode`: `ytd` and a custom Jan–Sep
  // have the SAME mode and the same bounds, and this line is where they stop
  // being the same thing.
  if (isPeriodPresetKey(presetKey)) return { [PERIOD_PARAM]: presetKey };

  const mode = period?.mode;
  if (mode === "all") return { [PERIOD_PARAM]: "all" };

  if (mode === MONTH_TOKEN) {
    const month = period.to || period.from;
    if (!isMonthValue(month)) return {};
    return { [PERIOD_PARAM]: MONTH_TOKEN, [PERIOD_MONTH_PARAM]: monthToken(month) };
  }

  if (mode === CUSTOM_TOKEN) {
    if (!isMonthValue(period.from) || !isMonthValue(period.to)) return {};
    return {
      [PERIOD_PARAM]: CUSTOM_TOKEN,
      [PERIOD_FROM_PARAM]: monthToken(period.from),
      [PERIOD_TO_PARAM]: monthToken(period.to),
    };
  }

  return {};
}

/**
 * `{period, invalid}` for a `URLSearchParams`.
 *
 * `invalid` is true only when the URL ASKED for a period and the answer could not
 * be built — an unknown preset, a malformed month, a reversed range. It is false
 * for a URL that simply carries no period, because no period is not a mistake:
 * it is the default (§19 — arriving at the page does not rewrite its address).
 *
 * `now` stays injectable so the preset branches are testable without freezing a
 * date, and it is an INSTANT read as Swiss wall-clock by `presetToPeriod` — this
 * parser introduces no second notion of "today" (Regel 51).
 */
export function parsePeriodParams(searchParams, now) {
  const fallback = () => ({ period: defaultPeriod(now), invalid: true });
  const raw = searchParams.get(PERIOD_PARAM);

  if (raw == null) return { period: defaultPeriod(now), invalid: false };

  // A preset is re-derived, not read back from stored bounds. That is what keeps
  // `?period=ytd` meaning "this year so far" in December as well as in January.
  if (isPeriodPresetKey(raw)) return { period: presetToPeriod(raw, now), invalid: false };

  if (raw === MONTH_TOKEN) {
    const month = parseMonthToken(searchParams.get(PERIOD_MONTH_PARAM));
    if (!month) return fallback();
    return { period: { mode: "month", from: month, to: month, presetKey: null }, invalid: false };
  }

  if (raw === CUSTOM_TOKEN) {
    const from = parseMonthToken(searchParams.get(PERIOD_FROM_PARAM));
    const to = parseMonthToken(searchParams.get(PERIOD_TO_PARAM));
    if (!from || !to) return fallback();
    // Von after Bis. The picker refuses to emit one; a hand-written URL can still
    // carry it, and every window selector downstream would read it as an empty
    // period rather than as an error.
    if (ordinal(from) > ordinal(to)) return fallback();
    return { period: { mode: "custom", from, to, presetKey: null }, invalid: false };
  }

  return fallback();
}

/** A copy of `params` with this module's four keys removed. */
function withoutPeriodParams(params) {
  const next = new URLSearchParams(params);
  for (const key of PERIOD_PARAMS) next.delete(key);
  return next;
}

/**
 * `{period, setPeriod}` — the page's period, backed by the query string.
 *
 * `setPeriod` PUSHES a history entry, the same choice `useInvoiceView` makes for
 * the invoice workspace: the requirement is that Back returns to the previously
 * selected period rather than leaving the page.
 */
export default function usePeriodParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const { period, invalid } = useMemo(() => parsePeriodParams(searchParams), [searchParams]);

  // A period the contract cannot read is dropped from the address, so the URL
  // stops claiming a selection the page is not showing. REPLACE, not push: this
  // is the page correcting itself, and Back must return to wherever the reader
  // came from rather than to the broken link they arrived on. It runs once —
  // after it, `invalid` is false — and it cannot start a loop with the memo above
  // because it only ever removes keys that memo rejected.
  useEffect(() => {
    if (!invalid) return;
    setSearchParams((prev) => withoutPeriodParams(prev), { replace: true });
  }, [invalid, setSearchParams]);

  const setPeriod = useCallback(
    (next) => {
      setSearchParams((prev) => {
        // Built from `prev`, so every parameter this module does not own survives
        // the change — a tab, a filter, a campaign tag. Only the four period keys
        // are cleared, and only the new period's keys are written back.
        const params = withoutPeriodParams(prev);
        for (const [key, value] of Object.entries(serializePeriod(next))) {
          params.set(key, value);
        }
        return params;
      });
    },
    [setSearchParams]
  );

  return { period, setPeriod };
}
